'use client';

// The phone app's offline sync (BL-2344, ADR 066 — Offline Mode § 5): opens the device store for
// the signed-in tenant and user, owns one sync engine, and fires it on app open, on return to the
// foreground, on reconnect, every 5 minutes while open and online, and after a queue Retry or
// Overwrite. Mounted under the signed-in shell. Outside the native app it is inert. Each run sends
// the change queue first (BL-2345), which this provider also hands to the Storage & Sync page and
// offline writes. It reads the queue once per change and shares the rows with the page's change
// queue, the bar and the sidebar (BL-2438, BL-2685). Back online, it tells screens when the reconnect's
// upload pass has finished, so they re-read the server with the queued changes in it (BUG-2111).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { OFFLINE_SYNC_POLL_MS } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-sync-eligibility';
import {
  buildChangeQueueGroups,
  type ChangeQueueGroupView,
} from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-display';
import type { QueueStatus } from '@auctogon/admin-isomorphic/isomorphic/offline/queue-status';
import type { NativeTransferResolveInput } from '../../gateways/native-device';
import { useDerivedQueueStatus } from './useDerivedQueueStatus';
import { useQueueEntries } from './useQueueEntries';
import { useNativeTransferStatus } from './useNativeTransferStatus';
import { useOpenStorageSyncOnTap } from './useOpenStorageSyncOnTap';
import { api } from '../../api-client/typed-client';
import {
  createReopeningScopeHandles,
  getNativeDeviceGateway,
  NativeCapabilityUnavailableError,
  OFFLINE_SESSION_RECORD_KEY,
  type NativeDeviceGateway,
  type OfflineMediaStaging,
  type OfflineScope,
  type OutboxEntry,
} from '../../gateways/native-device';
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh';
import { createOfflineSyncApi } from '../../stores/offline-sync/offline-sync-api';
import { createChangeQueueReplay, type ChangeQueueActionResult, type ChangeQueueReplay } from '../../stores/change-queue/change-queue-replay';
import { migrateLegacyQueue } from '../../stores/change-queue/migrate-legacy-queue';
import { openNativeMediaQueue } from '../../stores/change-queue/native-media-queue';
import { createReplayTransport } from '../../stores/change-queue/replay-transport';
import {
  createOfflineSyncEngine,
  type OfflineSyncEngine,
  type OfflineSyncState,
} from '../../stores/offline-sync/offline-sync-engine';
import { registerOfflineWriteQueue } from '../../api-client/offline-transport/offline-write-queue';
import { useAuth } from '../auth/AuthProvider';
import { useActiveTenantId } from '../tenant/TenantProvider';
import { useOfflineMode } from './OfflineModeProvider';
import { useReportChangeQueueCounts } from './ChangeQueueCountBridge';
import { BackOnlineReplayContext, type BackOnlineReplayValue } from './back-online-replay';
import { isActiveOfflineScope } from './offline-session';

/** The page-level sync status line. */
export type OfflineSyncStatus = 'syncing' | 'failed' | 'synced';

/** What {@link useOfflineSync} returns. */
export interface OfflineSyncValue {
  /** True once the device store is open in the native app. Every control hides until then. */
  available: boolean;
  /** The status line, or null when unavailable. */
  status: OfflineSyncStatus | null;
  /** True when the failed status is the device running out of storage (BUG-2065). */
  storageFull: boolean;
  /** The engine's state, or null when unavailable. */
  state: OfflineSyncState | null;
  /** @returns the auction's switch position on this device (no choice recorded means on). */
  isEnabled: (auctionId: string) => boolean;
  /** @returns whether the auction is downloading right now. */
  isDownloading: (auctionId: string) => boolean;
  /** Flip one auction's switch on this device. Off is also the Storage sheet's delete (BL-2566). */
  setEnabled: (input: { auctionId: string; enabled: boolean }) => Promise<void>;
  /**
   * The change queue (BL-2345), or null when unavailable. Retry and Overwrite also send the change
   * straight away when the app is online, and start a sync (BUG-2463).
   */
  changeQueue: ChangeQueueReplay | null;
}

const NOOP_ASYNC = async (): Promise<void> => {};

const INERT: OfflineSyncValue = {
  available: false,
  status: null,
  storageFull: false,
  state: null,
  isEnabled: () => false,
  isDownloading: () => false,
  setEnabled: NOOP_ASYNC,
  changeQueue: null,
};

const OfflineSyncContext = createContext<OfflineSyncValue>(INERT);

/** @returns the sync controls and state; inert outside the native app or before the store opens. */
export function useOfflineSync(): OfflineSyncValue {
  return useContext(OfflineSyncContext);
}

/**
 * What the device holds for its saved auctions (BL-2566): every saved auction's size added up, its
 * lots as UTF-8 JSON plus its thumbnails (ADR 066 — Offline Mode § 2). Staged files and the
 * reference data are not counted, so the total equals the Storage sheet's rows added up. The one
 * gap is brief: an auction still on the device but gone from the tenant's list (deleted on the
 * server) counts here until the next sync drops it, while the sheet already leaves it out.
 *
 * @returns the byte count, or null while offline sync is unavailable.
 */
export function useOfflineStorageUsed(): number | null {
  const { available, state } = useContext(OfflineSyncContext);
  if (!available || state == null) return null;
  return Object.values(state.auctions).reduce((sum, auction) => sum + auction.bytes, 0);
}

/** @returns the change queue (BL-2345), or null outside the native app or before the store opens. */
export function useChangeQueue(): ChangeQueueReplay | null {
  return useContext(OfflineSyncContext).changeQueue;
}

const logClientError = (message: string, err: Error) => {
  // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
  console.error(message, err);
};

/** The change queue as the Offline page and the counts read it (BL-2438). */
export interface ChangeQueueEntriesValue {
  /** Every queued entry, in queue order; null until the queue is read (or with no queue). */
  entries: OutboxEntry[] | null;
  /** Every lot's desired state (BL-2681), read with `entries`; null until the queue is read. */
  lotStates: LotDesiredState[] | null;
  /** The groups over `entries` and `lotStates`; empty until they are read. */
  groups: ChangeQueueGroupView[];
  /**
   * What the page shows (BL-2685): whether anything waits and which changes failed, over the JS
   * queue and the native one. Null until both are read, or while a read has failed.
   */
  status: QueueStatus | null;
  /** Retry or discard failed native rows (BL-2685). */
  resolveNative: (input: NativeTransferResolveInput) => Promise<ChangeQueueActionResult>;
  /** The last read's failure (or, before the store opens, its failure to open), or null. */
  error: Error | null;
  /** True while the device store is still opening (the queue is not readable yet, but will be). */
  opening: boolean;
  /** Read the queue again (the tab's Retry after a failed read). */
  refresh: () => void;
}

const NO_ENTRIES: ChangeQueueEntriesValue = {
  entries: null,
  lotStates: null,
  groups: [],
  status: { pending: false, failures: [], issues: 0 },
  resolveNative: async () => ({ success: false, code: 'NOT_FOUND' }),
  error: null,
  opening: false,
  refresh: () => {},
};

const ChangeQueueEntriesContext = createContext<ChangeQueueEntriesValue>(NO_ENTRIES);

/** @returns the queued entries and the tab's rows over them (BL-2438). */
export function useChangeQueueEntries(): ChangeQueueEntriesValue {
  return useContext(ChangeQueueEntriesContext);
}

/** The engine and the change queue it uploads, built together for one tenant and user. */
interface SyncParts {
  engine: OfflineSyncEngine;
  queue: ChangeQueueReplay;
}

/** Staged files are optional: an app build without the Filesystem plugin queues no file adds. */
async function openMediaOrNull(gateway: NativeDeviceGateway, scope: OfflineScope): Promise<OfflineMediaStaging | null> {
  try {
    return await gateway.openMediaStaging(scope);
  } catch (err) {
    if (err instanceof NativeCapabilityUnavailableError) return null;
    throw err;
  }
}

/**
 * Opens the store and builds the engine for one tenant and user.
 *
 * @returns the engine and its change queue, or null until ready (or forever outside the native app).
 */
function useSyncEngine(input: { enabled: boolean; tenantId: string | null; userId: string | null; isOnline: () => boolean }) {
  const { enabled, tenantId, userId, isOnline } = input;
  const [parts, setParts] = useState<SyncParts | null>(null);
  // A gateway with no device store (a plain browser) never opens one: the queue is unavailable, not opening.
  // A non-production browser emulating the app (BL-2450) opens the real IndexedDB store instead.
  const [unavailable, setUnavailable] = useState(false);
  // A store that failed to open for any other reason is an error with a Retry, never an endless
  // "opening". Keyed on the attempt it belongs to, so a Retry clears it.
  const [attempt, setAttempt] = useState(0);
  const [startError, setStartError] = useState<{ attempt: number; error: Error } | null>(null);
  const retryStart = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || tenantId == null || userId == null) return;
    let live = true;
    let ready = false;
    let built: SyncParts | null = null;
    let unregister: (() => void) | null = null;
    let unwatchNative: (() => void) | null = null;
    (async () => {
      const gateway = await getNativeDeviceGateway();
      const scope = { tenantId, userId };
      const open = async () => ({ store: await gateway.openOfflineStore(scope), media: await openMediaOrNull(gateway, scope) });
      // BUG-2065: a connection the web view drops is opened again on the next call, never held dead.
      const { store, media } = createReopeningScopeHandles({ initial: await open(), reopen: open, isLive: () => live });
      // A cleanup that ran while the store was opening builds and registers nothing for this scope (BUG-2147).
      if (!live) return;
      // BL-2681: lot changes an earlier build queued become each lot's desired state, before any write queues.
      await migrateLegacyQueue(store);
      if (!live) return;
      // BUG-2064: only the device's active session is replayed, which another tab can move.
      const readScopeSession = () => store.records.get({ collection: 'session', key: OFFLINE_SESSION_RECORD_KEY });
      const isActiveScope = () => isActiveOfflineScope(scope, { readScopeSession });
      // BL-2683: the native queue queued photos are handed to, or null while the switch is off (the default) or the build has none.
      const native = await openNativeMediaQueue({ gateway, scope, logError: logClientError });
      if (!live) return;
      const queue = createChangeQueueReplay({
        store,
        media,
        native,
        transport: createReplayTransport({ api, tenantId }),
        isOnline,
        isActiveScope,
        logError: logClientError,
      });
      const next = createOfflineSyncEngine({
        store,
        api: createOfflineSyncApi({ api, tenantId }),
        uploader: queue,
        isOnline,
        isActiveScope,
        // BUG-2124: photos download only while the app is open and in use. Back in the
        // foreground, the foreground run picks them up where they stopped.
        isInUse: () => document.visibilityState === 'visible',
      });
      built = { engine: next, queue };
      // A native photo row finishing releases its lot's state request, so the lot is sent now rather than at the next trigger.
      // Trailing debounce: a batch of rows finishing is one sync run, not one per row.
      let wake: ReturnType<typeof setTimeout> | null = null;
      const unsubscribe = native?.onChange(() => {
        if (wake != null) clearTimeout(wake);
        wake = setTimeout(() => void next.run({ kind: 'foreground' }), NATIVE_WAKE_DEBOUNCE_MS);
      });
      unwatchNative = () => {
        if (wake != null) clearTimeout(wake);
        unsubscribe?.();
      };
      // BL-2431: offline writes queue here, so the tab and the counts see them at once. BL-2447: a
      // file picked offline is staged in the same scope's staging. BUG-2147: registered as soon as the
      // store is open, before the engine's first load. On a very large store that load can take
      // minutes or fail, and an offline Save must still queue meanwhile rather than be lost.
      unregister = registerOfflineWriteQueue({ scope: store.scope, queueLocalWrite: (write) => queue.queueLocalWrite(write), media });
      await next.load();
      if (!live) return;
      ready = true;
      setParts({ engine: next, queue });
      await next.run({ kind: 'open' });
    })().catch((err: unknown) => {
      // A gateway with no device store (a plain browser) refuses: the sync simply stays off.
      if (err instanceof NativeCapabilityUnavailableError) {
        if (live) setUnavailable(true);
        return;
      }
      const error = err instanceof Error ? err : new Error(String(err));
      // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
      console.error('offline sync: could not start', error);
      if (live && !ready) setStartError({ attempt, error });
    });
    return () => {
      live = false;
      // A pass still sending this scope's changes must not carry on under the next session, and
      // neither may the engine running it (BUG-2064).
      built?.queue.close();
      built?.engine.stop();
      unregister?.();
      unwatchNative?.();
      setParts(null);
    };
  }, [enabled, tenantId, userId, isOnline, attempt]);

  return { parts, unavailable, startError: startError?.attempt === attempt ? startError.error : null, retryStart };
}

/** How long the native queue must be quiet before its changes wake a sync run. */
const NATIVE_WAKE_DEBOUNCE_MS = 1000;

const EMPTY_SUBSCRIBE = () => () => {};

/**
 * @param props.children - the signed-in shell.
 * @returns the provider.
 */
export function OfflineSyncProvider({ children }: { children: ReactNode }) {
  const mode = useOfflineMode();
  const { user } = useAuth();
  const offlineRef = useRef(mode.isOffline);
  useEffect(() => {
    offlineRef.current = mode.isOffline;
  }, [mode.isOffline]);
  const isOnline = useCallback(() => !offlineRef.current, []);
  // Non-throwing: a shell rendered without a session (tests, the signed-out boot) is inert.
  const activeTenantId = useActiveTenantId();
  const { parts, unavailable, startError, retryStart } = useSyncEngine({
    enabled: mode.isNativeApp,
    tenantId: activeTenantId,
    userId: user?.id ?? null,
    isOnline,
  });
  const engine = parts?.engine ?? null;
  // Keyed on the parts alone: sync progress must not hand the queue's consumers a new object.
  const changeQueue = useMemo(() => (parts == null ? null : sendingAfterActions(parts)), [parts]);
  const state = useSyncExternalStore(
    engine?.subscribe ?? EMPTY_SUBSCRIBE,
    () => engine?.getState() ?? null,
    () => null,
  );

  const replays = useSyncTriggers({ engine, isOffline: mode.isOffline });
  const writesSent = state?.writesSent ?? 0;
  const replayValue = useMemo<BackOnlineReplayValue>(
    () => ({ available: engine != null, replays, writesSent }),
    [engine, replays, writesSent],
  );

  useOpenStorageSyncOnTap(mode.isNativeApp);
  const read = useQueueEntries(parts?.queue ?? null);
  const nativeStatus = useNativeTransferStatus({
    enabled: mode.isNativeApp,
    tenantId: activeTenantId,
    userId: user?.id ?? null,
  });
  const opening = mode.isNativeApp && parts == null && !unavailable && startError == null;
  const groups = useMemo(() => (read.entries == null ? [] : buildChangeQueueGroups(read.entries, read.lotStates ?? [])), [read.entries, read.lotStates]);
  const jsError = parts == null ? startError : read.error;
  // Before the store opens, the only possible error is the store failing to open: Retry opens it again.
  const error = jsError ?? nativeStatus.error;
  const jsReady = parts != null ? read.entries != null : !opening && startError == null;
  const status = useDerivedQueueStatus({ groups, native: nativeStatus.native, ready: error == null && jsReady && nativeStatus.settled });
  const { refresh: refreshNative } = nativeStatus;
  const queueValue = useMemo<ChangeQueueEntriesValue>(
    () => ({
      entries: read.entries,
      lotStates: read.lotStates,
      groups,
      status,
      resolveNative: nativeStatus.resolve,
      error,
      opening,
      refresh: () => {
        if (jsError != null) (parts == null ? retryStart : read.refresh)();
        else refreshNative();
      },
    }),
    [read.entries, read.lotStates, read.refresh, groups, status, nativeStatus.resolve, error, jsError, opening, parts, retryStart, refreshNative],
  );
  // The bar and the sidebar read the same derivation as the page (BL-2685), so the sidebar's count
  // equals the failure rows the page lists.
  useReportChangeQueueCounts(status == null ? null : { pending: status.pending, issues: status.issues });

  const value = useMemo<OfflineSyncValue>(() => {
    if (engine == null || state == null) return INERT;
    const status: OfflineSyncStatus =
      state.activity !== 'idle' ? 'syncing' : state.lastRunFailed ? 'failed' : 'synced';
    return {
      available: true,
      status,
      storageFull: status === 'failed' && state.storageFull,
      state,
      isEnabled: (auctionId) => state.settings[auctionId] ?? true,
      isDownloading: (auctionId) => state.downloading.has(auctionId),
      setEnabled: engine.setEnabled,
      changeQueue,
    };
  }, [engine, changeQueue, state]);

  return (
    <OfflineSyncContext.Provider value={value}>
      <ChangeQueueEntriesContext.Provider value={queueValue}>
        <BackOnlineReplayContext.Provider value={replayValue}>{children}</BackOnlineReplayContext.Provider>
      </ChangeQueueEntriesContext.Provider>
    </OfflineSyncContext.Provider>
  );
}

/**
 * Retry and Overwrite put a change back in line and send it now. The queue's own pass sends it at
 * once, because a sync under way queues the next run behind every auction's download (BUG-2463).
 * The sync still starts, so the downloads that follow the send still run.
 */
function sendingAfterActions({ engine, queue }: SyncParts): ChangeQueueReplay {
  const thenSync = async (result: Awaited<ReturnType<ChangeQueueReplay['retry']>>) => {
    if (!result.success) return result;
    // The row keeps its status when the send fails, and the sync's own upload tries it again.
    void queue.upload({ signal: new AbortController().signal }).catch((err: unknown) => {
      logClientError('change queue: the send after Retry or Overwrite failed', err instanceof Error ? err : new Error(String(err)));
    });
    void engine.run({ kind: 'sync-now' });
    return result;
  };
  return {
    ...queue,
    retry: async (id) => thenSync(await queue.retry(id)),
    overwrite: async (id) => thenSync(await queue.overwrite(id)),
  };
}

/**
 * Fires the engine on return to the foreground, on reconnect and every 5 minutes while visible
 * and online. App open is the engine's own first run.
 *
 * @returns how many reconnects have finished their upload pass (BUG-2111): screens re-read then.
 */
function useSyncTriggers({ engine, isOffline }: { engine: OfflineSyncEngine | null; isOffline: boolean }): number {
  useVisibilityRefresh(() => engine?.run({ kind: 'foreground' }), { enabled: engine != null && !isOffline });

  const [replays, setReplays] = useState(0);
  const wasOffline = useRef(isOffline);
  /** True while a reconnect's pass is still running and its re-read not yet released. */
  const replayPending = useRef(false);
  useEffect(() => {
    const reconnected = wasOffline.current && !isOffline;
    wasOffline.current = isOffline;
    if (replayPending.current) {
      replayPending.current = false;
      // The engine changed (a tenant or user switch) while that pass ran: nothing will answer it
      // now, so the waiting screens re-read. Offline again, they wait for the next reconnect.
      if (!isOffline) void Promise.resolve().then(() => setReplays((n) => n + 1));
    }
    if (!reconnected || engine == null) return;
    // Asked before the run starts, so the pass waited for is this reconnect's (or the follow-up
    // it queues behind a run under way), never one that began before the edge.
    const uploaded = engine.afterNextUpload();
    void engine.run({ kind: 'reconnect' });
    replayPending.current = true;
    // Offline again (or a new engine) before the pass ends: that pass no longer answers this edge,
    // so it must not release the re-read the next reconnect is waiting for.
    let current = true;
    void uploaded.then(() => {
      if (!current) return;
      replayPending.current = false;
      setReplays((n) => n + 1);
    });
    return () => {
      current = false;
    };
  }, [engine, isOffline]);

  useEffect(() => {
    if (engine == null || isOffline) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void engine.run({ kind: 'interval' });
    }, OFFLINE_SYNC_POLL_MS);
    return () => clearInterval(timer);
  }, [engine, isOffline]);

  return replays;
}
